import { MessageSignatureDialog } from "../../components/MessageSignatureButton";
import { Trans, useLingui } from "@lingui/react/macro";
import type { MessageReaction, ThreadMessage } from "@rakazo/contracts";
import { canReactToThreadMessage, MESSAGE_REACTIONS } from "@rakazo/contracts";
import { canMarkMessageUnread } from "@rakazo/core";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@rakazo/ui-web";
import {
  Copy,
  MessageSquareDot,
  MoreHorizontal,
  Reply,
  Smile,
  ShieldCheck,
  Square,
  Volume2,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { MessageHoverMetadata } from "../../components/MessageHoverMetadata";
import { copyableMessageText } from "../../lib/message-text";
import { errorText } from "../../lib/user-error";

export function MessageHoverActions({
  message,
  side,
  onReply,
  onMarkUnread,
  markedUnread = false,
  onReact,
  onSpeak,
  speaking = false,
  onDeleteMessage,
}: {
  message: ThreadMessage;
  side: "start" | "end";
  onReply: (message: ThreadMessage) => void;
  onMarkUnread: (message: ThreadMessage) => void;
  markedUnread?: boolean;
  onReact: (message: ThreadMessage, reaction: MessageReaction) => Promise<void>;
  onSpeak?: () => void;
  speaking?: boolean;
  onDeleteMessage?: (message: ThreadMessage) => Promise<void>;
}) {
  const { t } = useLingui();
  const [moreOpen, setMoreOpen] = useState(false);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Streaming progress bubbles keep hover free for selection / stop clicks.
  if (message.id.startsWith("progress:")) return null;

  function copyMessage() {
    const text = copyableMessageText(message);
    if (!text || !navigator.clipboard) return;
    void navigator.clipboard.writeText(text).catch(() => undefined);
  }

  const iconButtonClass =
    "grid h-11 w-11 place-items-center text-muted-foreground transition-colors hover:text-foreground [@media(hover:hover)_and_(pointer:fine)]:h-7 [@media(hover:hover)_and_(pointer:fine)]:w-7";

  return (
    <>
      <MessageHoverMetadata
        pinned={moreOpen || reactionsOpen || speaking || signatureOpen || deleteOpen}
        side={side}
      >
        <div data-testid="message-hover-actions" className="flex items-center gap-0.5">
          {canReactToThreadMessage(message) ? (
            <Popover open={reactionsOpen} onOpenChange={setReactionsOpen}>
              <PopoverTrigger aria-label={t`React`} className={iconButtonClass}>
                <Smile size={15} strokeWidth={1.7} />
              </PopoverTrigger>
              <PopoverContent
                align={side === "end" ? "start" : "end"}
                className="w-auto flex-row gap-0 rounded-2xl p-1.5"
                aria-label={t`Reactions`}
              >
                {MESSAGE_REACTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    aria-label={emoji}
                    className="grid h-11 w-11 place-items-center rounded-xl text-2xl hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring"
                    onClick={() => {
                      setReactionsOpen(false);
                      void onReact(message, emoji);
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          ) : null}
          <button
            type="button"
            aria-label={t`Reply`}
            onClick={() => onReply(message)}
            className={iconButtonClass}
          >
            <Reply size={15} strokeWidth={1.7} />
          </button>
          {onSpeak ? (
            <button
              type="button"
              aria-label={speaking ? t`Stop speaking` : t`Speak this reply`}
              title={speaking ? t`Stop speaking` : t`Speak this reply`}
              onClick={onSpeak}
              className={iconButtonClass}
            >
              {speaking ? (
                <Square size={15} strokeWidth={1.7} />
              ) : (
                <Volume2 size={15} strokeWidth={1.7} />
              )}
            </button>
          ) : null}
          <DropdownMenu open={moreOpen} onOpenChange={setMoreOpen}>
            <DropdownMenuTrigger aria-label={t`More`} className={iconButtonClass}>
              <MoreHorizontal size={15} strokeWidth={1.7} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align={side === "end" ? "start" : "end"} className="w-auto">
              <DropdownMenuItem
                onClick={() => {
                  setMoreOpen(false);
                  onReply(message);
                }}
              >
                <Reply size={14} strokeWidth={1.7} />
                <Trans>Reply</Trans>
              </DropdownMenuItem>
              {canMarkMessageUnread(message) && !markedUnread ? (
                <DropdownMenuItem
                  onClick={() => {
                    setMoreOpen(false);
                    onMarkUnread(message);
                  }}
                >
                  <MessageSquareDot size={14} strokeWidth={1.7} />
                  <Trans>Mark as unread</Trans>
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onClick={copyMessage}>
                <Copy size={14} strokeWidth={1.7} />
                <Trans>Copy</Trans>
              </DropdownMenuItem>
              {message.role === "bot" && message.blocks.some((block) => block.kind === "text") ? (
                <DropdownMenuItem
                  onClick={() => {
                    setMoreOpen(false);
                    setSignatureOpen(true);
                  }}
                >
                  <ShieldCheck size={14} strokeWidth={1.7} />
                  <Trans>Signed text</Trans>
                </DropdownMenuItem>
              ) : null}
              {onDeleteMessage &&
              message.role === "user" &&
              message.canDelete === true &&
              !message.deleted ? (
                <DropdownMenuItem
                  variant="destructive"
                  onClick={() => {
                    setMoreOpen(false);
                    setDeleteError(null);
                    setDeleteOpen(true);
                  }}
                >
                  <Trash2 size={14} strokeWidth={1.7} />
                  <Trans>Delete message</Trans>
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </MessageHoverMetadata>
      <MessageSignatureDialog
        source="thread"
        messageId={message.id}
        open={signatureOpen}
        onOpenChange={setSignatureOpen}
      />
      <AlertDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (!deleting) setDeleteOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Delete this message?</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>This removes your message from the chat. The bot’s replies are kept.</Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError ? (
            <p role="alert" className="text-sm text-destructive">
              {deleteError}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={deleting}
              onClick={async () => {
                if (!onDeleteMessage || deleting) return;
                setDeleting(true);
                setDeleteError(null);
                try {
                  await onDeleteMessage(message);
                  setDeleteOpen(false);
                } catch (error) {
                  setDeleteError(errorText(error));
                } finally {
                  setDeleting(false);
                }
              }}
            >
              {deleting ? <Trans>Deleting…</Trans> : <Trans>Delete message</Trans>}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
